import { Injectable, inject } from '@angular/core';

import { AppData } from '../data';
import { THEME_KEY } from '../types/theme.type';
import { environment } from 'src/environments/environment';
import { Platform } from '@ionic/angular';
import { StatusBar, Style } from '@capacitor/status-bar';
import { Capacitor } from '@capacitor/core';

@Injectable({ providedIn: 'root' })
export class ThemeService {
  private readonly _data = inject(AppData);
  private readonly _platform = inject(Platform);

  public async handleTheme(): Promise<boolean> {
    await this._platform.ready();

    const theme = localStorage.getItem('ParaScopeTheme');
    const defaultTheme = environment.features.defaultTheme;

    if (theme) {
      const isDark = theme === 'dark';
      await this.toggleDarkTheme(isDark);
      return isDark;
    }

    if (defaultTheme) {
      const isDark = defaultTheme === 'dark';
      await this.toggleDarkTheme(isDark);
      return isDark;
    }

    // Use matchMedia to check the user preference
    const prefersDark = window.matchMedia('(prefers-color-scheme: dark)');
    // Initialize the dark theme based on the initial
    // value of the prefers-color-scheme media query
    const isDark = prefersDark.matches || defaultTheme === 'dark' || theme === 'dark';
    // Listen for changes to the prefers-color-scheme media query
    prefersDark.addEventListener('change', mediaQuery => this.toggleDarkTheme(mediaQuery.matches));

    if (Capacitor.isNativePlatform()) {
      // Enable overlays so your web view content extends under the status bar if desired
      await StatusBar.setOverlaysWebView({ overlay: false });
    }

    await this.toggleDarkTheme(isDark)
    return isDark;
  }

  public async toggleDarkTheme(isDark: boolean): Promise<void> {
    localStorage.setItem(THEME_KEY, isDark ? 'dark' : 'light');
    document.body.classList.toggle('dark', isDark);
    if (Capacitor.isNativePlatform()) {
      if (isDark) {
        // Dark theme: Dark background, light icons/text
        await StatusBar.setStyle({ style: Style.Dark });
        await StatusBar.setBackgroundColor({ color: '#121212' }); // Set your app's dark background color
      } else {
        // Light theme: Light background, dark icons/text
        await StatusBar.setStyle({ style: Style.Light });
        await StatusBar.setBackgroundColor({ color: '#ffffff' });
      }
    }
    this._data.set('isDark', isDark);
  }
}
